import { Box, Container, Grid, Link, Typography, styled } from "@mui/material"
import { useContext } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router"

import OriginalsLogo from "@sap/assets/logo/Originals"
import SatellietLogo from "@sap/assets/logo/Satelliet"
import { formatDateLocalized } from "@sap/utils/date"

import allLangs from "@/locales/langs"
import { DomainContext } from "@/routes/providers/originals"
import { useAuthStore } from "@/store/auth"

const StyledLink = styled(Link)(({ theme }) => ({
  display: "block",
  borderRadius: 4,
  cursor: "pointer",
  position: "relative",
  padding: "0.3rem 0rem",
  color: theme.palette.grey[500],
  "&:hover": {
    color: theme.palette.grey[100],
  },
}))

const now = new Date()
const versionDate = new Date(now.getFullYear(), now.getMonth(), 1)

const Footer = () => {
  const { t, i18n } = useTranslation()
  const { isOriginals } = useContext(DomainContext)
  const { user } = useAuthStore((state) => state)

  const currentLang = allLangs.find((lang) => lang.value === i18n.language)

  return (
    <Box
      component={"footer"}
      sx={{
        bgcolor: "grey.900",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          p: "1rem",
          color: "white",
        }}
      >
        <Box
          sx={{
            py: 10,
            overflow: "hidden",
          }}
        >
          <Grid container spacing={3}>
            <Grid item lg={4} md={6} sm={6} xs={12}>
              <Link to="/" component={RouterLink} sx={{ display: "contents" }}>
                {isOriginals ? (
                  <OriginalsLogo height={60} inverted sx={{ mb: 2 }} />
                ) : (
                  <SatellietLogo
                    height={60}
                    inverted
                    sx={{ mb: 2 }}
                    isStaff={user?.is_staff || false}
                  />
                )}
              </Link>

              <Typography
                variant="body1"
                sx={{
                  mb: 1.5,
                  color: "grey.500",
                }}
              >
                {t("footer.pricingExclVAT")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 1.5,
                  color: "grey.500",
                }}
              >
                {t("Version")} {formatDateLocalized(versionDate, "PPP", currentLang?.dateLocales)}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 1.5,
                  color: "grey.500",
                }}
              >
                © {now.getFullYear()}{" "}
                {isOriginals ? "Satelliet Originals" : "Satelliet Meubelen B.V."}
              </Typography>
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <Box
                sx={{
                  fontSize: "18px",
                  fontWeight: "600",
                  mb: 1.5,
                  lineHeight: "1",
                  color: "white",
                }}
              >
                {t("footer.statements")}
              </Box>

              <div>
                <StyledLink href={t("footer.salesConditionsUrl")} target="_blank" rel="noopener">
                  {t("footer.salesConditions")}
                </StyledLink>
                <StyledLink href={t("footer.cookieStatementurl")} target="_blank" rel="noopener">
                  {t("footer.cookieStatement")}
                </StyledLink>
                <StyledLink href={t("footer.privacyStatementUrl")} target="_blank" rel="noopener">
                  {t("footer.privacyStatement")}
                </StyledLink>
              </div>
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <Box
                sx={{
                  fontSize: "18px",
                  fontWeight: "600",
                  mb: 1.5,
                  lineHeight: "1",
                  color: "white",
                }}
              >
                {isOriginals ? "Satelliet Originals" : "Satelliet"}
              </Box>

              <div>
                <StyledLink
                  href={
                    isOriginals
                      ? "https://satellietoriginals.net/originals/"
                      : t("footer.aboutSatellietUrl")
                  }
                  target="_blank"
                  rel="noopener"
                >
                  {isOriginals ? t("footer.aboutSatellietOriginals") : t("footer.aboutSatelliet")}
                </StyledLink>
                <StyledLink href={t("footer.sustainabilityUrl")} target="_blank" rel="noopener">
                  {t("footer.sustainability")}
                </StyledLink>
                <StyledLink href={t("footer.showroomsUrl")} target="_blank" rel="noopener">
                  {t("footer.showrooms")}
                </StyledLink>
                <StyledLink href={t("footer.contactUrl")} target="_blank" rel="noopener">
                  {t("footer.contact")}
                </StyledLink>
              </div>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
